import React, { useState } from 'react';
import { View, StyleSheet, Animated } from 'react-native';
import {
  Card,
  Text,
  Button,
  IconButton,
  useTheme,
  Chip,
} from 'react-native-paper';
import { Lightbulb, X, ChevronRight, Info } from 'lucide-react-native';
import type { ContextualTip } from '../../store/slices/dashboardPersonalizationSlice';

interface ContextualTipCardProps {
  tip: ContextualTip;
  onDismiss: () => void;
  onAction: (action: string) => void;
  compact?: boolean;
  showPriority?: boolean;
}

export function ContextualTipCard({
  tip,
  onDismiss,
  onAction,
  compact = false,
  showPriority = false,
}: ContextualTipCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [fadeAnim] = useState(new Animated.Value(1));

  const handleDismiss = () => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      onDismiss();
    });
  };

  const handleAction = (action: string) => {
    onAction(action);
  };

  const getTipIcon = () => {
    switch (tip.type) {
      case 'onboarding':
        return <ChevronRight size={20} color={theme.colors.primary} />;
      case 'feature':
        return <Info size={20} color={theme.colors.secondary} />;
      case 'improvement':
        return <Lightbulb size={20} color={theme.colors.tertiary} />;
      case 'troubleshooting':
        return <Info size={20} color={theme.colors.error} />;
      default:
        return <Lightbulb size={20} color={theme.colors.primary} />;
    }
  };

  const getTipColor = () => {
    switch (tip.type) {
      case 'onboarding':
        return theme.colors.primaryContainer;
      case 'feature':
        return theme.colors.secondaryContainer;
      case 'improvement':
        return theme.colors.tertiaryContainer;
      case 'troubleshooting':
        return theme.colors.errorContainer;
      default:
        return theme.colors.surfaceVariant;
    }
  };

  const getTextColor = () => {
    switch (tip.type) {
      case 'onboarding':
        return theme.colors.onPrimaryContainer;
      case 'feature':
        return theme.colors.onSecondaryContainer;
      case 'improvement':
        return theme.colors.onTertiaryContainer;
      case 'troubleshooting':
        return theme.colors.onErrorContainer;
      default:
        return theme.colors.onSurfaceVariant;
    }
  };

  const cardStyle = [
    styles.card,
    { backgroundColor: getTipColor() },
    compact && styles.compactCard,
  ];

  const textColor = getTextColor();

  return (
    <Animated.View style={[styles.container, { opacity: fadeAnim }]}>
      <Card style={cardStyle} mode='contained'>
        <Card.Content style={styles.content}>
          <View style={styles.header}>
            <View style={styles.titleRow}>
              {getTipIcon()}
              <Text
                variant={compact ? 'bodyMedium' : 'titleSmall'}
                style={[styles.title, { color: textColor }]}
                numberOfLines={compact ? 1 : 2}
              >
                {tip.title}
              </Text>
              {showPriority && (
                <Chip
                  compact
                  style={[
                    styles.priorityChip,
                    { backgroundColor: theme.colors.surface },
                  ]}
                  textStyle={{ fontSize: 10 }}
                >
                  {Math.round(tip.priority * 100)}%
                </Chip>
              )}
            </View>

            {tip.dismissible && (
              <IconButton
                icon={() => <X size={16} color={textColor} />}
                size={16}
                onPress={handleDismiss}
                style={styles.dismissButton}
              />
            )}
          </View>

          {!compact && (
            <Text
              variant='bodySmall'
              style={[styles.content, { color: textColor }]}
              numberOfLines={expanded ? undefined : 2}
            >
              {tip.content}
            </Text>
          )}

          {!compact && tip.content.length > 100 && (
            <Button
              mode='text'
              compact
              onPress={() => setExpanded(!expanded)}
              style={styles.expandButton}
              labelStyle={{ color: textColor, fontSize: 12 }}
            >
              {expanded ? 'Show Less' : 'Show More'}
            </Button>
          )}

          <View style={styles.actions}>
            {tip.type === 'onboarding' && (
              <Button
                mode='contained-tonal'
                compact
                onPress={() => handleAction('start_tutorial')}
                style={styles.actionButton}
                labelStyle={{ fontSize: 12 }}
              >
                Get Started
              </Button>
            )}

            {tip.type === 'feature' && (
              <Button
                mode='contained-tonal'
                compact
                onPress={() => handleAction('try_feature')}
                style={styles.actionButton}
                labelStyle={{ fontSize: 12 }}
              >
                Try It
              </Button>
            )}

            {tip.type === 'improvement' && (
              <Button
                mode='contained-tonal'
                compact
                onPress={() => handleAction('apply_suggestion')}
                style={styles.actionButton}
                labelStyle={{ fontSize: 12 }}
              >
                Apply
              </Button>
            )}

            {tip.type === 'troubleshooting' && (
              <Button
                mode='contained-tonal'
                compact
                onPress={() => handleAction('fix_issue')}
                style={styles.actionButton}
                labelStyle={{ fontSize: 12 }}
              >
                Fix
              </Button>
            )}

            <Button
              mode='text'
              compact
              onPress={() => handleAction('learn_more')}
              style={styles.learnMoreButton}
              labelStyle={{ color: textColor, fontSize: 12 }}
            >
              Learn More
            </Button>
          </View>
        </Card.Content>
      </Card>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: 4,
  },
  card: {
    elevation: 2,
  },
  compactCard: {
    elevation: 1,
  },
  content: {
    paddingVertical: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 8,
  },
  title: {
    flex: 1,
    fontWeight: '600',
  },
  priorityChip: {
    height: 20,
  },
  dismissButton: {
    margin: 0,
    padding: 0,
  },
  contentText: {
    marginBottom: 8,
    lineHeight: 20,
  },
  expandButton: {
    alignSelf: 'flex-start',
    marginTop: 4,
    marginBottom: 8,
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  actionButton: {
    minWidth: 80,
  },
  learnMoreButton: {
    marginLeft: 'auto',
  },
});
